import { computed, inject, provide, ssrRef, toRef, useContext } from '@nuxtjs/composition-api';
import { useDialog } from '~/composables/useModal';

export const conversationStoreKey = Symbol('conversationStore');

export function installConversationContext() {
  /**
   * Conversation State
   */
  const state = ssrRef(() => ({
    // conversations
    isConversationsLoading: false,
    conversations: [],
    conversationsPagination: {},
    selectedConversation: null,
    // messages
    isMessagesLoading: false,
    isMessageSending: false,
    messages: [],
    messagesPagination: {},
    conversationsCount: 0,
  }));

  const { $service } = useContext();
  const { openErrorDialog } = useDialog();

  async function getConversations(page) {
    state.value.isConversationsLoading = true;
    try {
      const { data, pagination: paginationData } = await $service.conversation.getConversationList({ page });
      const filteredConversations = data.filter((conversation) => conversation.participants.length > 1); // conversation 2 kişi olmak zorunda. kişi sayısı 2'den az ise listeden kaldır
      state.value.conversations = page ? state.value.conversations.concat(filteredConversations) : filteredConversations;
      state.value.conversationsPagination = paginationData;
    } catch (error) {
      openErrorDialog({ error });
    } finally {
      state.value.isConversationsLoading = false;
    }
  }

  function resetMessages() {
    state.value.isMessagesLoading = false;
    state.value.isMessageSending = false;
    state.value.messages = [];
    state.value.messagesPagination = {};
  }

  async function getMessages(page) {
    state.value.isMessagesLoading = true;
    try {
      const { data, pagination: paginationData } = await $service.conversation.getMessages({
        id: state.value.selectedConversation._id,
        page,
      });
      data.messages = data.messages.reverse();
      if (page) {
        state.value.messages.unshift(...data.messages);
      } else {
        state.value.messages = data.messages;
      }
      state.value.messagesPagination = paginationData;
    } catch (error) {
      openErrorDialog({ error });
    } finally {
      state.value.isMessagesLoading = false;
    }
  }

  /**
   * Add a message to messages list
   *
   * @param {object} message message object
   */
  function addMessage(message) {
    const currentConversationIndex = state.value.conversations.findIndex((conversation) => {
      return conversation._id === state.value.selectedConversation._id;
    });
    if (currentConversationIndex === -1) {
      state.value.conversations.unshift(message);
    } else {
      state.value.conversations[currentConversationIndex].messages.unshift(message);
    }
    state.value.messages.push(message);
    return true;
  }

  /**
   * Add a draft conversation
   *
   * @param {object} conversation conversation object
   */
  function addDraftConversation(conversation) {
    state.value.conversations.unshift(conversation);
  }

  /**
   * Remove last message from messages list
   */
  function removeLastMessage() {
    return state.value.messages.pop();
  }

  /**
   * Send Message to selected conversation
   * @param {string} message message text
   */
  async function sendMessage(message) {
    state.value.isMessageSending = true;
    try {
      const result = await $service.conversation.sendMessage({ id: state.value.selectedConversation._id, message });
      addMessage(result.data);
      return result;
    } catch (error) {
      openErrorDialog({ error });
    } finally {
      state.value.isMessageSending = false;
    }
  }

  /**
   * Send Message to selected conversation
   * @param {string} message message text
   */
  async function createConversation(recipentId) {
    try {
      const { data } = await $service.conversation.createConversation(recipentId);
      return { data };
    } catch (error) {
      openErrorDialog({ error });
    }
  }

  /**
   * Send Message to selected conversation
   * @param {string} message message text
   */
  async function createConversationWithMessage(recipentId, message) {
    try {
      const { data } = await $service.conversation.createConversationWithMessage(recipentId, message);
      return { data };
    } catch (error) {
      openErrorDialog({ error });
    }
  }

  /**
   * Find conversation
   * @param {string} message message text
   */
  async function findConversation(recipentId) {
    try {
      const { data } = await $service.conversation.findConversation(recipentId);
      return { data };
    } catch (error) {
      openErrorDialog({ error });
    }
  }

  /**
   * Delete a message
   *
   * @param {string} conversationId
   * @param {string} messageId
   */
  async function deleteMessage(conversationId, messageId) {
    const result = await $service.conversation.deleteMessage(conversationId, messageId);
    state.value.messages = state.value.messages.filter((item) => item._id !== messageId);
    return result;
  }

  /**
   * Delete a conversation
   *
   * @param {string} conversationId
   */
  async function deleteConversation(conversationId) {
    if (conversationId) {
      await $service.conversation.deleteConversation(conversationId);
    }
    state.value.conversations = state.value.conversations.filter((item) => item._id !== conversationId);
    state.value.selectedConversation = null;
    resetMessages();
  }

  const changeSelectedConversation = (value) => {
    state.value.selectedConversation = value;
  };

  async function getConversationsCount() {
    try {
      const { data } = await $service.conversation.getConversationsCount();
      state.value.conversationsCount = data;
    } catch (error) {}
  }

  // ...toRefs(state.value) de yapılabilir ama import ederken intellisense desteği olmadığı için tercih edilmedi.
  provide(conversationStoreKey, {
    state,
    // conversations
    isConversationsLoading: computed(() => state.value.isConversationsLoading),
    conversations: computed(() => state.value.conversations),
    selectedConversation: computed(() => state.value.selectedConversation),
    conversationsPagination: computed(() => state.value.conversationsPagination),
    getConversations,
    createConversation,
    createConversationWithMessage,
    addDraftConversation,
    findConversation,
    deleteConversation,
    changeSelectedConversation,
    // messages
    isMessagesLoading: computed(() => state.value.isMessagesLoading),
    isMessageSending: computed(() => state.value.isMessageSending),
    messages: computed(() => state.value.messages),
    messagesPagination: computed(() => state.value.messagesPagination),
    getMessages,
    deleteMessage,
    sendMessage,
    addMessage,
    removeLastMessage,
    resetMessages,
    conversationsCount: toRef(state.value, 'conversationsCount'),
    getConversationsCount,
  });
}

/**
 * @typedef {Object} Conversation
 * @property {import("@nuxtjs/composition-api").Ref.<boolean>} isConversationsLoading
 * @property {import("@nuxtjs/composition-api").Ref.<array>} conversations
 * @property {import("@nuxtjs/composition-api").Ref.<object>} selectedConversation
 * @property {import("@nuxtjs/composition-api").Ref.<object>} conversationsPagination
 * @property {function} getConversations
 * @property {function} createConversation
 * @property {function} createConversationWithMessage
 * @property {function} findConversation
 * @property {function} addDraftConversation
 * @property {function} deleteConversation
 * @property {function} changeSelectedConversation
 * @property {import("@nuxtjs/composition-api").Ref.<boolean>} isMessagesLoading
 * @property {import("@nuxtjs/composition-api").Ref.<boolean>} isMessageSending
 * @property {import("@nuxtjs/composition-api").Ref.<array>} messages
 * @property {import("@nuxtjs/composition-api").Ref.<object>} messagesPagination
 * @property {function} getMessages,
 * @property {function} deleteMessage,
 * @property {function} sendMessage,
 * @property {function} addMessage,
 * @property {function} removeLastMessage
 * @property {function} resetMessages
 * @property {function} getConversationsCount
 */

/**
 * @returns {Conversation} Conversation State
 */
export function useConversation() {
  return inject(conversationStoreKey);
}
